import { Injectable, signal } from '@angular/core';
import { ConversationState } from 'src/app/services/learner/audio-call-control.service';
import { SubtitleKind } from 'src/app/utils/enums/learner/subtitle-kind.enum';
import { PathwayConstants } from 'src/app/utils/constants/pathway/pathway.constants';
import { PathwayStepType } from 'src/app/utils/enums/learner/pathway.enum';
import { TranslationService } from 'src/app/services/translation.service';

export interface SubtitleContext {
  callFlow: string;
  stepType: string;
  conversationState: ConversationState | null;
  isAvatarSpeaking: boolean;
  isVideoPlaying: boolean;
  isMicOn: boolean;
  callStarted: boolean;
  isAvatarThinking: boolean;
  quizResultTtsPlaying: boolean;
  isPreamble: boolean;
  captionText: string;
}

export interface SubtitleState {
  kind: SubtitleKind;
  text: string;
}

@Injectable({ providedIn: 'root' })
export class PathwaySubtitleService {
  private readonly POST_SPEAKING_GRACE_MS = 5000;
  private postSpeakingTimer: ReturnType<typeof setTimeout> | null = null;

  readonly isInPostSpeakingGrace = signal(false);
  lastSpokenCaption = '';

  constructor(private readonly translationService: TranslationService) {}

  startGracePeriod(currentCaption: string): void {
    this.clearGracePeriod();
    this.lastSpokenCaption = currentCaption;
    this.isInPostSpeakingGrace.set(true);
    this.postSpeakingTimer = setTimeout(() => {
      this.isInPostSpeakingGrace.set(false);
      this.postSpeakingTimer = null;
    }, this.POST_SPEAKING_GRACE_MS);
  }

  clearGracePeriod(): void {
    if (this.postSpeakingTimer) {
      clearTimeout(this.postSpeakingTimer);
      this.postSpeakingTimer = null;
    }
    this.isInPostSpeakingGrace.set(false);
  }

  reset(): void {
    this.clearGracePeriod();
    this.lastSpokenCaption = '';
  }

  getSubtitle(ctx: SubtitleContext): SubtitleState {
    const kind = this.resolveKind(ctx);
    const text = this.resolveText(ctx, kind);
    return { kind, text };
  }

  private resolveKind(ctx: SubtitleContext): SubtitleKind {
    if (ctx.callFlow === PathwayConstants.CALL_FLOW_QUIZ_RESULT) {
      return ctx.quizResultTtsPlaying ? SubtitleKind.Speaking : SubtitleKind.Ready;
    }

    if (
      ctx.conversationState === ConversationState.AGENT_SPEAKING ||
      ctx.isAvatarSpeaking ||
      ctx.isVideoPlaying
    ) {
      return SubtitleKind.Speaking;
    }

    if (!ctx.isMicOn) return SubtitleKind.Muted;

    if (!ctx.callStarted) return SubtitleKind.Thinking;

    if (ctx.conversationState === ConversationState.USER_SPEAKING) return SubtitleKind.Listening;

    if (this.isInPostSpeakingGrace()) return SubtitleKind.Speaking;

    if (ctx.conversationState === ConversationState.PROCESSING || ctx.isAvatarThinking) {
      return SubtitleKind.Thinking;
    }

    return SubtitleKind.Ready;
  }

  private resolveText(ctx: SubtitleContext, kind: SubtitleKind): string {
    if (kind === SubtitleKind.Muted) {
      return this.translationService.translate('FOOTER_SUBTITLE_MUTED', 'learningPathway');
    }

    if (kind === SubtitleKind.Listening) {
      return this.translationService.translate('FOOTER_SUBTITLE_LISTENING', 'learningPathway');
    }

    if (kind === SubtitleKind.Thinking) {
      if (!ctx.callStarted) return '';
      return this.translationService.translate('FOOTER_SUBTITLE_THINKING', 'learningPathway');
    }

    if (kind === SubtitleKind.Ready) {
      if (ctx.callFlow === PathwayConstants.CALL_FLOW_QUIZ_RESULT) {
        return this.translationService.translate('FOOTER_SUBTITLE_QUIZ_RESULT_DONE', 'learningPathway');
      }
      if (ctx.stepType === PathwayStepType.LEARN_WITH_X && !ctx.isPreamble) {
        return this.translationService.translate('FOOTER_SUBTITLE_READY', 'learningPathway');
      }
    }

    if (this.isInPostSpeakingGrace()) return this.lastSpokenCaption;

    return ctx.captionText;
  }
}
